// The two AppBar shapes the Flutter app uses on every screen.
//
// CenterTitleBar — a centred bold title in the 'BetterFonts' family
//   (util.dart appBar_heading): Favourites, Locations, Scenes, Me.
//
// ProfileTitleBar — a dense ListTile: circular profile avatar, bold title,
//   smaller subtitle. Used by Rooms, Devices and every device control screen.
//
// Both sit on BackColor with FontColor icons, elevation 5, black shadow.

import React from 'react';
import {
  AppBar, Toolbar, Typography, Box, IconButton, Avatar, useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../../core/state/AppContext';

const HEADING_FONT = "'BetterFonts', 'Satisfy', cursive";

// The running version, small and unobtrusive, so anyone can tell at a glance
// which build a phone has. This tracks the BUNDLE, so after an OTA it shows the
// new number — which is how you confirm an OTA actually landed on a device.
function useBarSx() {
  const theme = useTheme();
  return {
    bgcolor: theme.sli.back,
    color: theme.sli.text,
    boxShadow: '0 2px 5px rgba(0,0,0,0.6)',
    backgroundImage: 'none',
  };
}

// The version tag used to sit here as a 9px overlay in the bar's corner. It now
// lives in the company footer (ui/mobile/components/MobileShell) — one place,
// next to the other support detail, instead of on top of every screen's title.
export function CenterTitleBar({ title, actions, showBack = false }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const sx = useBarSx();
  return (
    <AppBar position="sticky" elevation={5} sx={sx}>
      <Toolbar sx={{ position: 'relative' }}>
        {showBack && (
          <IconButton edge="start" onClick={() => navigate(-1)} sx={{ color: theme.sli.text }}>
            <ArrowBackIcon />
          </IconButton>
        )}
        <Box sx={{ flex: 1, textAlign: 'center' }}>
          <Typography
            sx={{
              color: theme.sli.text,
              fontWeight: 900,
              fontFamily: HEADING_FONT,
              fontSize: 20,
            }}
          >
            {title}
          </Typography>
        </Box>
        {actions}
      </Toolbar>
    </AppBar>
  );
}

// `onBack` overrides the history pop. A screen that knows WHERE it sits in the
// structure should say so — history and structure only agree when the user
// arrived the long way round, and never after a deep link or a reload. See
// core/devices/deviceNav.js.
export function ProfileTitleBar({ title, subtitle, actions, showBack = true, onBack }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { profileUrl } = useApp();
  const sx = useBarSx();

  return (
    <AppBar position="sticky" elevation={5} sx={sx}>
      <Toolbar sx={{ gap: 1, position: 'relative' }}>
        {showBack && (
          <IconButton
            edge="start"
            onClick={() => (onBack ? onBack() : navigate(-1))}
            sx={{ color: theme.sli.text }}
          >
            <ArrowBackIcon />
          </IconButton>
        )}
        <Avatar src={profileUrl || undefined} sx={{ width: 40, height: 40 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography noWrap sx={{ fontSize: 18, color: '#fff', fontWeight: 700 }}>
            {title}
          </Typography>
          {subtitle != null && (
            <Typography noWrap sx={{ fontSize: 14, color: '#fff' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {actions}
      </Toolbar>
    </AppBar>
  );
}
